

/** @jsxImportSource react */
import React, { useEffect } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { HashRouter } from 'react-router-dom'
import { Navbar } from './components/Navbar'
import { LandingPage } from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import SignUpPage from './pages/SignUpPage'
import { I18nProvider, useTranslation } from './i18n';
import { AuthProvider, useAuth } from './contexts/auth';
import { EmailVerificationModal } from './components/EmailVerificationModal';

// Authenticated Pages
import Layout from './components/Layout'
import DashboardPage from './pages/DashboardPage'
import OrdersPage from './pages/OrdersPage'
import { OrderDetailsPage } from './pages/OrderDetailsPage'
import NewShippingRequestPage from './pages/NewShippingRequestPage'
import ReportsPage from './pages/ReportsPage'
import SettingsPage from './pages/SettingsPage'
import UsersPage from './pages/UsersPage'
import ProfilePage from './pages/ProfilePage'
import NotificationsPage from './pages/NotificationsPage'
import { AccountantReportPage } from './pages/AccountantReportPage'
import { IncomingOrdersPage } from './pages/IncomingOrdersPage'
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import PriceCheckListPage from './pages/PriceCheckListPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InvoicePage from './pages/InvoicePage';
import TrackShipmentPage from './pages/TrackShipmentPage';
import LockedAccountPage from './pages/LockedAccountPage';

const AppRoutes: React.FC = () => {
  const { user, isInitialized } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = t('tagExpress');
  }, [t]);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id)
    if (section) {
      const headerOffset = 80;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
         top: offsetPosition,
         behavior: "smooth"
      });
    }
  }

  if (!isInitialized) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // BLOCKING: If user account is locked, show the locked page and nothing else.
    if (user.isLocked) {
      return <LockedAccountPage />;
    }
    
    // BLOCKING: If user is logged in but email is not verified, show the verification modal ONLY.
    // unless the user is actually on the verification success page.
    if (!user.isEmailVerified && location.pathname !== '/verify-email') {
        return <EmailVerificationModal />;
    }

    // Hide main layout for standalone pages or invoice printing
    if (location.pathname.startsWith('/invoice/') || location.pathname === '/verify-email') {
        return (
            <Routes>
                <Route path="/invoice/:id" element={<InvoicePage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
            </Routes>
        );
    }
      
    // Authenticated user routes
    return (
      <Layout>
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailsPage />} />
          <Route path="/shipping/new" element={<NewShippingRequestPage />} />
          <Route path="/track" element={<TrackShipmentPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/notification-preferences" element={<NotificationPreferencesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/accountant-report" element={<AccountantReportPage />} />
          <Route path="/incoming-orders" element={<IncomingOrdersPage />} />
          <Route path="/price-check-list" element={<PriceCheckListPage />} />
          {/* Redirects for logged-in users */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/signup" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    );
  }

  // Unauthenticated user routes
  return (
    <>
      <Navbar scrollToSection={scrollToSection} />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
       <HashRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
      </HashRouter>
    </I18nProvider>
  )
}